import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useGamesData } from "entities/game/hooks/useGamesData";
import { useSelectedGamesInModuleQuery } from "entities/game/queries/useSelectedGamesInModuleQuery";
import { Game } from "entities/game/types";
import { useMetadataTagsListQuery } from "entities/metadataTags/queries/useMetadataTagsListQuery";
import { useUploadContentBlocksImages } from "entities/modules/hooks/useUploadContentBlocksImages";
import { useUploadSingleImage } from "entities/modules/hooks/useUploadSingleImage";
import { useModuleSelectedStudiosQuery } from "entities/modules/queries/useModuleSelectedStudiosQuery";
import { useTagBlocksStore } from "entities/modules/stores/useTagBlocksStore";
import { GamesOrderType, ModuleGame, ModuleType } from "entities/modules/types";
import {
  defaultTopPrivacyTipsBlock,
  isOneCardBlockModuleLayout,
  isThreeCardBlockModuleTypeLayout,
} from "entities/modules/utils";
import { useStudiosListQuery } from "entities/studios/queries/useStudiosListQuery";
import { useTagsListQuery } from "entities/tags/queries/useTagsListQuery";
import { useThemeTagsListQuery } from "entities/themeTags/queries/useThemeTagListQuery";
import { sortBy } from "lodash";
import {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createContext } from "shared/utils/context";
import {
  getAlgoliaTagsFilters,
  serializeModuleTagsToBlocks,
} from "widgets/ModuleTagsWidget/utils";
import { useShallow } from "zustand/react/shallow";
import { useModulesPage } from "../../../context";
import { useGeneralData } from "../stores/useGeneralData";
import { useSelectedGamesData } from "../stores/useSelectedGamesData";
import { useStudioOrderingData } from "../stores/useStudioOrderingData";
import { useStudiosData } from "../stores/useStudiosData";
import {
  MODULE_ONE_CARD_BLOCKS,
  MODULE_THREE_CARDS_BLOCKS,
  MODULE_TOP_PRIVACY_TIPS_BLOCK,
} from "./constants";
import { ModuleDetailsTabs, ModuleDetailsType } from "./types";
import { formatSelectedGames, formatStudios } from "./utils";

const [Provider, useModuleDetails] = createContext<ModuleDetailsType>({
  name: "ModuleDetailsContext",
  hookName: "useModuleDetails",
  providerName: "ModuleDetailsProvider",
});

const ModuleDetailsProvider: FC<PropsWithChildren> = (props) => {
  const { allowed: hasReadAccess } = useRolePermission("moduleTemplates:read");
  const { allowed: hasReadTranslationsAccess } = useRolePermission(
    "moduleTemplates:translations:read",
  );
  const { moduleDetailsDrawer } = useModulesPage();
  const moduleData = moduleDetailsDrawer.drawerDetails;
  const moduleId = (moduleData?.id || moduleData || null) as string | null;
  const [selectedTab, setSelectedTab] = useState(ModuleDetailsTabs.General);

  // TODO: refactor! hook serves the data from TABLE and their search operations
  const { tableData: allGames, isLoading: isLoadingAllGames } = useGamesData({
    isActive: ["true"],
    size: 1000,
  });

  const { data: studios, isLoading: isLoadingStudios } = useStudiosListQuery();

  const { data: metadataTags = [] } = useMetadataTagsListQuery();
  const { data: themeTags = [] } = useThemeTagsListQuery();
  const { data: tags = [], isLoading: isLoadingTags } = useTagsListQuery();
  const initialRenderRef = useRef(false);

  const {
    setModuleName,
    setAdminNotes,
    setTranslations,
    setModuleType,
    setModuleLayout,
    setStudioFilterEnabled,
    clearGeneralData,
    setBaseUrl,
    setContentBlocks,
    setBonusObtainType,
    setRows,
    contentBlocks,
    moduleLayout,
    setOneCardBlockImage,
    setThreeCardBlockImages,
    setIconShown,
    setIcon,
    moduleType,
    topPrivacyTipsBlock,
    setTopPrivacyTipsBlock,
    setTopPrivacyTipBlockImage,
  } = useGeneralData(
    useShallow((state) => ({
      contentBlocks: state.contentBlocks,
      moduleLayout: state.moduleLayout,
      setModuleName: state.setModuleName,
      setAdminNotes: state.setAdminNotes,
      setRows: state.setRows,
      setTranslations: state.setTranslations,
      setModuleType: state.setModuleType,
      setModuleLayout: state.setModuleLayout,
      setStudioFilterEnabled: state.setStudioFilterEnabled,
      setBaseUrl: state.setBaseUrl,
      setContentBlocks: state.setContentBlocks,
      setBonusObtainType: state.setBonusObtainType,
      clearGeneralData: state.clearGeneralData,
      setOneCardBlockImage: state.setOneCardBlockImage,
      setThreeCardBlockImages: state.setThreeCardBlockImages,
      setIconShown: state.setIconShown,
      setIcon: state.setIcon,
      moduleType: state.moduleType,
      topPrivacyTipsBlock: state.topPrivacyTipsBlock,
      setTopPrivacyTipsBlock: state.setTopPrivacyTipsBlock,
      setTopPrivacyTipBlockImage: state.setTopPrivacyTipBlockImage,
    })),
  );

  const { loadingQueries: threeCardsLoadingQueries } =
    useUploadContentBlocksImages({
      key: MODULE_THREE_CARDS_BLOCKS,
      moduleId,
      setCardBlockImages: setThreeCardBlockImages,
      contentBlocks,
      enabled: isThreeCardBlockModuleTypeLayout(moduleLayout),
    });

  const { loadingQueries: oneCardsLoadingQueries } =
    useUploadContentBlocksImages({
      key: MODULE_ONE_CARD_BLOCKS,
      moduleId,
      setCardBlockImages: setOneCardBlockImage,
      contentBlocks,
      enabled: isOneCardBlockModuleLayout(moduleLayout),
    });

  const { loadingQuery: topPrivacyTipsBlockImageLoadingQuery } =
    useUploadSingleImage({
      key: MODULE_TOP_PRIVACY_TIPS_BLOCK,
      moduleId,
      setCardBlockImage: setTopPrivacyTipBlockImage,
      backgroundImagePath: topPrivacyTipsBlock.backgroundImage,
      enabled: moduleType === ModuleType.TopPrivacyTips,
    });

  const {
    orderType,
    selectedGames,
    selectedGamesManually,
    selectedGamesByTag,
    setOrderType,
    setSelectedGames,
    setSelectedGamesByTag,
    setSelectedGamesManually,
    clearSelectedGamesData,
  } = useSelectedGamesData(
    useShallow((state) => ({
      orderType: state.orderType,
      selectedGames: state.selectedGames,
      selectedGamesManually: state.selectedGamesManually,
      selectedGamesByTag: state.selectedGamesByTag,
      setOrderType: state.setOrderType,
      setSelectedGames: state.setSelectedGames,
      setSelectedGamesByTag: state.setSelectedGamesByTag,
      setSelectedGamesManually: state.setSelectedGamesManually,
      clearSelectedGamesData: state.clearSelectedGamesData,
    })),
  );

  const { setSelectedStudios, clearStudiosData } = useStudiosData(
    useShallow((state) => ({
      setSelectedStudios: state.setSelectedStudios,
      clearStudiosData: state.clearStudiosData,
    })),
  );

  const { tagBlocks, setTagBlocks, clearTagsData } = useTagBlocksStore(
    useShallow((state) => ({
      tagBlocks: state.tagBlocks,
      setTagBlocks: state.setTagBlocks,
      clearTagsData: state.clearTagsData,
    })),
  );

  const { setOrderedStudios, clearStudioOrderingData } = useStudioOrderingData(
    useShallow((state) => ({
      setOrderedStudios: state.setOrderedStudios,
      clearStudioOrderingData: state.clearStudioOrderingData,
    })),
  );

  // TODO: refactor! Double pulling games on mount context, but only with selected tags
  const {
    data: selectedGamesList = [],
    isLoading: isLoadingSelectedGamesList,
    isFetching: isFetchingSelectedGamesList,
  } = useSelectedGamesInModuleQuery({
    moduleId,
    filters: getAlgoliaTagsFilters(tagBlocks),
  });

  const studioIds = useMemo(() => {
    const studiosIds = new Set<number>();
    selectedGames.forEach((game) => {
      if (game.studioId) {
        studiosIds.add(game.studioId);
      }
    });

    return Array.from(studiosIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGames.length]);

  const { data: availableStudios, isLoading: isLoadingAvailableStudios } =
    useModuleSelectedStudiosQuery(studioIds);

  useEffect(() => {
    if (!moduleData) {
      console.warn("Cannot find module template");
      return;
    }

    setModuleName(moduleData.name);
    setAdminNotes(moduleData.adminNotes ?? "");
    setTranslations(moduleData.translations ?? {});
    setModuleType(moduleData.type);
    setModuleLayout(moduleData.layout);
    setOrderType(moduleData.gamesOrderType || GamesOrderType.Random);
    setSelectedStudios(formatStudios(moduleData, studios));
    setStudioFilterEnabled(moduleData.studioFilterEnabled ?? false);
    setBaseUrl(moduleData.baseUrl ?? "");
    setContentBlocks(moduleData.contentBlocks);
    setTopPrivacyTipsBlock(
      moduleData.topPrivacyTipsBlock ?? defaultTopPrivacyTipsBlock(),
    );
    setRows(moduleData.rows || 1);
    setBonusObtainType(moduleData.bonusObtainType ?? null);

    const tagBlocks = serializeModuleTagsToBlocks({
      moduleTagTypes: moduleData.tagTypes,
      moduleTags: moduleData.tags,
      tags: tags ?? [],
      metadataTags: metadataTags ?? [],
      themeTags: themeTags ?? [],
      tagsMatchRules: moduleData.tagsMatchRules ?? [],
    });

    setTagBlocks(tagBlocks);

    if (moduleData.icon) {
      setIconShown(true);
      setIcon(moduleData.icon);
    }

    // disabled, because we need to track only for loading state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingTags, isLoadingStudios, moduleId]);

  useEffect(() => {
    if (hasReadAccess) {
      return;
    }

    if (hasReadTranslationsAccess) {
      setSelectedTab(ModuleDetailsTabs.Translations);
    }
  }, [hasReadAccess, hasReadTranslationsAccess, moduleId]);

  useEffect(() => {
    const shouldUpdateGames =
      !isLoadingAllGames &&
      moduleData?.selectedGames.length &&
      !!availableStudios;

    if (shouldUpdateGames) {
      const manualGames = moduleData.selectedGames
        .map((selectedGame: ModuleGame) =>
          allGames.data.find((game) => game.id.toString() === selectedGame.id),
        )
        .filter(Boolean) as Game[];

      const formattedGames = formatSelectedGames(
        manualGames,
        GamesOrderType.Manual,
        availableStudios,
        moduleData?.selectedGames,
      );

      if (!selectedGamesManually.length && !initialRenderRef.current) {
        setSelectedGamesManually(formattedGames);
        initialRenderRef.current = true;
      } else if (selectedGamesManually.length) {
        const orderFromModule = formattedGames.map((g) => g.id);
        const orderFromState = selectedGamesManually.map((g) => g.id);
        const orderChanged =
          JSON.stringify(orderFromModule) !== JSON.stringify(orderFromState);

        if (orderChanged) {
          setSelectedGamesManually(formattedGames);
        } else {
          const updatedGames = selectedGamesManually.map((game) => {
            const formattedGame = formattedGames.find(
              (formattedGame) => formattedGame.id === game.id,
            );

            if (formattedGame && formattedGame.studioName) {
              return {
                ...game,
                studioName: formattedGame.studioName,
              };
            }
            return game;
          });

          setSelectedGamesManually(updatedGames);
        }
      }
    }

    // disabled, because we do not need to track setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingAllGames,
    moduleData?.selectedGames,
    availableStudios?.length,
    initialRenderRef.current,
  ]);

  useEffect(() => {
    if (!selectedGamesManually.length || !availableStudios) return;

    const formattedGames = formatSelectedGames(
      selectedGamesManually
        .map((game) => {
          return allGames.data.find((g) => g.id.toString() === game.id);
        })
        .filter(Boolean) as Game[],
      orderType,
      availableStudios,
      moduleData?.selectedGames,
    );

    setSelectedGamesManually(formattedGames);

    // disabled, because we do not need to track setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType]);

  // Prepopulate selectedGamesByTag from selectedGamesList
  useEffect(() => {
    const shouldUpdateGames =
      !isLoadingSelectedGamesList && selectedGamesList && !!availableStudios;

    if (shouldUpdateGames) {
      const sortedGames = sortBy(selectedGamesList, (game) => {
        const existingIdx = moduleData?.selectedGames?.findIndex(
          (selectedGame: { id: string }) =>
            game.id.toString() === selectedGame.id,
        );

        return existingIdx === -1 ? Infinity : existingIdx;
      });

      const formattedGames = formatSelectedGames(
        sortedGames,
        orderType,
        availableStudios,
        moduleData?.selectedGames,
      );

      setSelectedGamesByTag(formattedGames);
    }

    // disabled, because we do not need to track setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderType,
    isLoadingSelectedGamesList,
    selectedGamesList,
    moduleData?.selectedGames,
    availableStudios?.length,
  ]);

  useEffect(() => {
    // Rerender on every selectedGamesManually or selectedGamesByTag change
    const combinedGames = [...selectedGamesManually, ...selectedGamesByTag];

    const uniqueGames = combinedGames.filter(
      (game, index, self) => index === self.findIndex((g) => g.id === game.id),
    );

    setSelectedGames(uniqueGames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGamesManually, selectedGamesByTag]);

  useEffect(() => {
    if (!availableStudios || isLoadingAvailableStudios) {
      return;
    }

    const sortedStudios = sortBy(availableStudios, (studio) => {
      const existingIdx = moduleData?.orderedGamesStudiosIds.findIndex(
        (studioId: string) => {
          return studio.id.toString() === studioId;
        },
      );

      return existingIdx === -1 ? Infinity : existingIdx;
    });

    const formattedStudios = sortedStudios.map((studio, idx) => {
      const selectedGamesCount = selectedGames.filter(
        (game) => game.studioId === studio.id,
      ).length;

      return {
        id: studio.id.toString(),
        name: studio.name,
        gamesCount: selectedGamesCount,
        order: idx + 1,
      };
    });

    setOrderedStudios(formattedStudios);

    // disabled, because we do not need to track setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    availableStudios,
    isLoadingAvailableStudios,
    moduleData?.orderedGamesStudiosIds,
    selectedGames,
  ]);

  const handleDrawerClose = () => {
    moduleDetailsDrawer.closeDrawer();
    setSelectedTab(ModuleDetailsTabs.General);
    clearGeneralData();
    clearSelectedGamesData();
    clearStudiosData();
    clearTagsData();
    clearStudioOrderingData();
    initialRenderRef.current = false;
  };

  const value: ModuleDetailsType = {
    allGames: allGames.data,
    moduleId,
    handleDrawerClose,
    selectedTab,
    setSelectedTab,
    moduleData: moduleData ?? null,
    isLoadingSelectedGamesList,
    isFetchingSelectedGamesList,
    isLoadingAvailableStudios,
    oneCardsLoadingQueries,
    availableStudios,
    threeCardsLoadingQueries,
    topPrivacyTipsBlockImageLoadingQuery,
  };

  return <Provider value={value}>{props.children}</Provider>;
};

export { ModuleDetailsProvider, useModuleDetails };
